/**
 * FritsOnboarding — uitleg voor nieuwe gebruikers (eerste bezoek) en daarna
 * altijd terug te openen via het ?-icoon in de header. Puur informatief.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack, Box, Chip, Collapse,
} from '@mui/material';

const STORAGE_KEY = 'frits-onboarding-v1-seen';

export function shouldShowOnboarding() {
  try { return !localStorage.getItem(STORAGE_KEY); } catch { return false; }
}

export function markOnboardingSeen() {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* quota */ }
}

const SECTIONS = [
  {
    icon: '📬', title: 'Wat doet Frits?',
    body: 'Elke ochtend om 07:30 neemt Frits je inbox door. Per mail krijg je: prioriteit, een korte samenvatting, '
      + 'de categorie (beantwoorden, taak, planning, offerte, fyi) en — waar nodig — een concept-antwoord in jouw '
      + 'eigen schrijfstijl. Wil je tussendoor verversen: knop "Check nu".',
  },
  {
    icon: '✋', title: 'Frits verstuurt nooit zelf',
    body: 'Alle antwoorden staan als concept (draft) in je Outlook-thread. Versturen doe je altijd zelf — in Outlook, '
      + 'of via "Verstuur & klaar" (kan alleen naar interne collega\'s). Ook plannings- en reminder-mails zijn altijd concepten.',
  },
  {
    icon: '💬', title: 'Chat direct met Frits',
    body: 'Onder elke mail zit een chatveld. Frits kent de hele thread — óók reacties van ná deze mail. '
      + 'Vraag bijvoorbeeld "heb ik hierop geantwoord?", "maak een reminder" of "maak het concept korter". '
      + 'Een aangepast concept zet je met één klik in de draft.',
  },
  {
    icon: '⚡', title: 'Acties en afhandelen',
    body: 'De knoppenrij toont maximaal 6 acties die bij déze mail passen; de rest zit achter "Meer". '
      + 'Afhandelen: Klaar & volgende (Enter), Overslaan (S) of Archiveren (A). Op mobiel: swipe → klaar, ← overslaan. '
      + 'Druk ? voor alle sneltoetsen.',
  },
  {
    icon: '🧠', title: 'Frits leert van jou',
    body: 'Klopt een inschatting niet? "Leer Frits" onthoudt het en past het vanaf de volgende run toe. '
      + 'Vaste opruim-acties per afzender stel je in via "Naar map" → "voortaan automatisch". '
      + 'Elke bewerking van een concept is een leersignaal.',
  },
  {
    icon: '🔔', title: 'Meldingen',
    body: 'Zet de ochtend-push aan (belletje rechtsboven) en je krijgt een melding zodra je triage klaarstaat — '
      + 'ook op je telefoon als je de app als website-app hebt geïnstalleerd.',
  },
];

const INSTALL_STEPS = [
  { platform: '📱 iPhone / iPad (Safari)', steps: 'Open intern.fullframe.nl in Safari → tik op het Deel-icoon (vierkant met pijl) → "Zet op beginscherm" → Voeg toe.' },
  { platform: '🤖 Android (Chrome)', steps: 'Open intern.fullframe.nl in Chrome → menu (⋮ rechtsboven) → "App installeren" (of "Toevoegen aan startscherm").' },
  { platform: '💻 Mac / Windows (Chrome of Edge)', steps: 'Open intern.fullframe.nl → klik op het installatie-icoon rechts in de adresbalk (schermpje met pijl) → Installeren. De app opent dan in een eigen venster.' },
];

export default function FritsOnboarding({ open, onClose }) {
  const [showInstall, setShowInstall] = useState(false);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        👋 Welkom bij Frits
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Je persoonlijke mail-assistent — zo werkt het.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {SECTIONS.map((s) => (
            <Stack key={s.title} direction="row" spacing={1.5} alignItems="flex-start">
              <Typography sx={{ fontSize: 22, lineHeight: 1.3 }}>{s.icon}</Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{s.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{s.body}</Typography>
              </Box>
            </Stack>
          ))}
          {/* 📲 Installeren als website-app (voor push op je telefoon) */}
          <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#eef2ff', border: '1px solid #c7d2fe' }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: '#4338ca', cursor: 'pointer', display: 'block' }}
              onClick={() => setShowInstall((v) => !v)}>
              {showInstall ? '▾' : '▸'} 📲 Zo installeer je dit als website-app (voor meldingen op je telefoon)
            </Typography>
            <Collapse in={showInstall}>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {INSTALL_STEPS.map((s) => (
                  <Box key={s.platform}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{s.platform}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.steps}</Typography>
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary">
                  Daarna: open Frits in de app en zet de ochtend-push aan via het belletje rechtsboven.
                </Typography>
              </Stack>
            </Collapse>
          </Box>

          <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography variant="caption" color="text.secondary">
              Handigste sneltoetsen:{' '}
              {[['Enter', 'klaar'], ['S', 'skip'], ['A', 'archief'], ['C', 'chat'], ['M', 'meer'], ['←', 'terug'], ['?', 'alles']].map(([k, l]) => (
                <Box key={k} component="span" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
                  <Chip label={k} size="small" sx={{ height: 18, fontSize: 10, fontFamily: 'monospace', fontWeight: 700, mr: 0.4 }} />{l}
                </Box>
              ))}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 800 }}>
          Aan de slag
        </Button>
      </DialogActions>
    </Dialog>
  );
}
